// Helpers for settings.yml fields that need a distinct react-hook-form `name`
// to avoid a registration collision with a same-named sibling field, but whose
// value must still be persisted under a canonical field name. Such a field
// declares `save_value_into: <canonical>` in settings.yml.
//
// Example: the printing-machine material-thickness inputs. The gsm `select`
// owns the canonical name (e.g. `printing_material_thickness_min_value`); the
// mm/in `length` input is renamed to `..._min_value_l` with
// `save_value_into: printing_material_thickness_min_value`. They are mutually
// exclusive via `visible_if`, but before the rename they shared one `name`, so
// react-hook-form treated them as one field and the value bled across units.

// Mirrors how FormFieldFactory decides field visibility: `eval`s the field's
// `visible_if` expression with `v`/`va` reading the supplied value bag.
// `visible_if` comes from the trusted, checked-in settings.yml — not user input.
const evaluateVisibleIf = (attribute, valueBag) => {
  if(attribute.visible_if == undefined) { return true; }

  // `v`/`va` are referenced from the eval'd expression string, not statically.
  const v = (key) => valueBag[key];                                       // eslint-disable-line no-unused-vars
  const va = (key) => valueBag['user_unit_price_attribute.' + key];       // eslint-disable-line no-unused-vars

  return eval(attribute.visible_if);
};

// On save: copy each `save_value_into` field's value into its canonical key,
// but only while that field is the active variant (visible_if against the live
// values), so a hidden variant's stale value never clobbers the active one
// (e.g. in gsm mode the mm `_l` field is skipped and the select's value stays).
//
// Mutates `values` in place on purpose: the caller POSTs that same object
// synchronously, so the copied value must be present without waiting for a
// React re-render — a `setValue` would only land after the next render.
const applySaveValueInto = (attributes, values) => {
  (attributes || []).forEach((attribute) => {
    if(attribute.save_value_into == undefined) { return; }
    if(evaluateVisibleIf(attribute, values)) {
      values[attribute.save_value_into] = values[attribute.name];
    }
  });
};

// On load: seed each `save_value_into` field from its canonical stored value so
// it renders (the canonical key is what the server persists; the split-off
// field is not). Guarded by `visible_if` evaluated against the freshly fetched
// `data` rather than live state — `setValue` is async, so right after the fetch
// loop the live values still hold the pre-fetch unit. Evaluating against `data`
// also means a record saved under the other variant (e.g. a gsm record) does
// not seed a stale cross-unit value into the hidden `_l` field.
const seedSaveValueIntoFields = (attributes, data, setValue) => {
  (attributes || []).forEach((attribute) => {
    if(attribute.save_value_into == undefined) { return; }
    if(data[attribute.save_value_into] == undefined) { return; }
    if(evaluateVisibleIf(attribute, data)) {
      setValue(attribute.name, data[attribute.save_value_into]);
    }
  });
};

export { evaluateVisibleIf, applySaveValueInto, seedSaveValueIntoFields };
